import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePageMeta, SITE_NAME } from '@/hooks/usePageMeta';
import { useHemisphere } from '@/context/HemisphereContext';
import NewsletterSignup from '@/components/newsletter/NewsletterSignup';
import { ascendingInHemisphere } from '@/lib/hemisphere';
import { MOON_PHASE_EMOJI, DAY_TYPE_META } from '@/types/biodynamic';
import type { MoonPhase, BiodynamicDayType } from '@/types/biodynamic';
import './Home.css';

const FEATURES = [
  {
    emoji: '🌱',
    title: 'Plant Database',
    description: 'Detailed cultivation guides for hundreds of vegetables, fruits, and herbs. From sowing depth to harvest, companion plants to organic pest control.',
    link: '/plants',
    linkLabel: 'Browse plants',
  },
  {
    emoji: '🌕',
    title: 'Biodynamic Calendar',
    description: 'Follow the lunar planting calendar with root, flower, fruit, and leaf days. Learn to make and apply biodynamic preparations for healthier soil and crops.',
    link: '/biodynamic',
    linkLabel: 'Explore biodynamic',
  },
  {
    emoji: '📖',
    title: 'Gardening Advice',
    description: 'Practical, organic techniques for better results — from soil improvement to crop rotation, seed saving to pruning. Rooted in tradition, informed by science.',
    link: '/advice',
    linkLabel: 'Read the guide',
  },
  {
    emoji: '🏡',
    title: 'My Garden',
    description: 'Record your garden details, track your plants, and keep a dated diary of everything you do. Monthly views let you look back over seasons and years.',
    link: '/my-garden',
    linkLabel: 'Start your garden',
  },
  {
    emoji: '🔍',
    title: 'Business Directory',
    description: 'Find local nurseries, garden designers, organic suppliers, and inspiring gardening blogs and vlogs near you. Leave reviews and support small businesses.',
    link: '/directory',
    linkLabel: 'Find a business',
  },
];

const SEASONAL_WISDOM = [
  { season: 'Spring', emoji: '🌸', quote: 'The soil is ready when it breaks apart in your fingers like chocolate cake.', attribution: 'Traditional' },
  { season: 'Summer', emoji: '☀️', quote: 'Water in the morning. Let the sun dry the leaves. Evening watering invites disease.', attribution: 'Old gardeners\' wisdom' },
  { season: 'Autumn', emoji: '🍂', quote: 'What you put back into the soil in autumn, the soil gives back to you in spring.', attribution: 'Traditional' },
  { season: 'Winter', emoji: '❄️', quote: 'The resting garden is not an idle garden — beneath the surface, life prepares itself.', attribution: 'Traditional' },
];

export default function Home() {
  const { isAuthenticated, loading, login } = useAuth();
  const { dataMonth } = useHemisphere();
  usePageMeta(
    SITE_NAME,
    'Organic and biodynamic gardening companion — moon planting calendar, growing guides for 52 vegetables and herbs, garden diary and monthly sowing advice.',
  );
  if (!loading && isAuthenticated) return <Navigate to="/dashboard" replace />;
  // dataMonth is 1-based; back to a 0-based index for the season bands below.
  const month = dataMonth(new Date().getMonth() + 1) - 1;
  const seasonIdx = month < 3 ? 3 : month < 6 ? 0 : month < 9 ? 1 : month < 12 ? 2 : 3;
  const quote = SEASONAL_WISDOM[seasonIdx];

  return (
    <div className="home">
      {/* Hero */}
      <section className="home-hero">
        <div className="home-hero-bg" aria-hidden="true">
          <HeroBotanicalBg />
        </div>
        <div className="home-hero-inner container">
          <div className="home-hero-content animate-fade-in">
            <p className="home-hero-eyebrow text-label">An organic & biodynamic gardening companion</p>
            <h1 className="home-hero-title text-display">
              The Gardeners<br />
              <em>Calendar</em>
            </h1>
            <p className="home-hero-lead">
              Grow better, season by season. Track your garden, follow lunar rhythms, and discover the wisdom of organic and biodynamic growing.
            </p>
            <div className="home-hero-actions">
              {isAuthenticated ? (
                <Link to="/dashboard" className="btn-primary-lg">
                  Go to my garden
                </Link>
              ) : (
                <>
                  <button className="btn-primary-lg" onClick={() => login()}>
                    Start your garden journal
                  </button>
                  <Link to="/plants" className="btn-ghost-lg">
                    Browse plants
                  </Link>
                </>
              )}
            </div>
          </div>

          <div className="home-hero-calendar animate-fade-in-delay-2">
            <TodayCard />
          </div>
        </div>

        {/* Wave bottom */}
        <div className="home-hero-wave" aria-hidden="true">
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 30 C240 60 480 0 720 30 C960 60 1200 0 1440 30 L1440 60 L0 60 Z" fill="var(--parchment)" />
          </svg>
        </div>
      </section>

      {/* Seasonal quote */}
      <section className="home-quote section-sm">
        <div className="container-narrow">
          <blockquote className="home-quote-block">
            <span className="home-quote-emoji">{quote.emoji}</span>
            <p>"{quote.quote}"</p>
            <cite>{quote.attribution}</cite>
          </blockquote>
        </div>
      </section>

      {/* Features */}
      <section className="home-features section">
        <div className="container">
          <div className="home-features-header">
            <p className="text-label" style={{ color: 'var(--sage)' }}>Everything in one place</p>
            <h2 className="text-headline">Your complete growing companion</h2>
          </div>

          <div className="home-features-grid">
            {FEATURES.map((feat, i) => (
              <div key={feat.title} className={`home-feature-card animate-fade-in-delay-${Math.min(i + 1, 5)}`}>
                <div className="home-feature-icon">{feat.emoji}</div>
                <h3 className="home-feature-title text-title">{feat.title}</h3>
                <p className="home-feature-desc text-body">{feat.description}</p>
                <Link to={feat.link} className="home-feature-link">
                  {feat.linkLabel} →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Biodynamic teaser */}
      <section className="home-biodynamic section">
        <div className="container">
          <div className="home-biodynamic-inner paper">
            <div className="home-biodynamic-text">
              <p className="text-label" style={{ color: 'var(--sage)' }}>Ancient wisdom, modern practice</p>
              <h2 className="text-headline">Grow with the moon</h2>
              <p className="text-body" style={{ color: 'var(--ink-light)', maxWidth: '50ch', marginTop: 'var(--space-4)' }}>
                Biodynamic gardening works with cosmic rhythms — planting root vegetables on root days, harvesting fruit on fruit days, making preparations from herbs and minerals to enliven the soil.
              </p>
              <p className="text-body" style={{ color: 'var(--ink-light)', maxWidth: '50ch', marginTop: 'var(--space-3)' }}>
                Pioneered by Rudolf Steiner in 1924 and developed through decades of careful observation by Maria Thun, biodynamic practice offers a profound way of understanding the garden as a living organism.
              </p>
              <Link to="/biodynamic" className="btn-sage" style={{ marginTop: 'var(--space-6)', display: 'inline-flex' }}>
                Discover biodynamic growing
              </Link>
            </div>
            <div className="home-biodynamic-visual" aria-hidden="true">
              <MoonCalendarIllustration />
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter — a smaller ask than an account, so it comes first */}
      <section className="home-newsletter section">
        <div className="container-narrow">
          <NewsletterSignup source="home" />
        </div>
      </section>

      {/* Sign-up CTA */}
      {!isAuthenticated && (
        <section className="home-cta section">
          <div className="container-narrow">
            <div className="home-cta-inner">
              <div className="home-cta-leaves" aria-hidden="true">🌿 🌱 🍃</div>
              <h2 className="text-headline">Start your garden journal today</h2>
              <p className="text-body" style={{ color: 'var(--ink-light)', maxWidth: '45ch', margin: '0 auto var(--space-8)' }}>
                Track your plants, keep a diary, and get personalised growing advice based on your zone, soil, and what you grow.
              </p>
              <div style={{ display: 'flex', gap: 'var(--space-4)', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button className="btn-primary-lg" onClick={() => login()}>
                  Create a free account
                </button>
                <Link to="/plants" className="btn-ghost-lg">
                  Browse the plant database
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

interface TodayAstro {
  phase: MoonPhase;
  illumination: number;
  dayType: BiodynamicDayType;
  ascending: boolean;
}

const phaseLabel = (p: MoonPhase) => {
  const words = p.replace(/-/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

function TodayCard() {
  const { dataMonth, hemisphere, isSouthern } = useHemisphere();
  const today = new Date();
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const [astro, setAstro] = useState<TodayAstro | null>(null);

  // The real calculation, loaded on demand. astronomy-engine is ~24 kB gzipped
  // and this is the page most first visits land on, so it is kept out of the
  // initial bundle and fills the card in once it arrives.
  useEffect(() => {
    let cancelled = false;
    import('@/lib/astronomy')
      .then(({ getDayAstronomy }) => {
        if (cancelled) return;
        const d = getDayAstronomy(new Date());
        setAstro({
          phase: d.moonPhase,
          illumination: d.moonIllumination,
          dayType: d.moonDayType,
          ascending: ascendingInHemisphere(d.moonAscending, hemisphere),
        });
      })
      .catch(() => {
        // Leave the card in its date-only state rather than showing a guess.
      });
    return () => { cancelled = true; };
  }, [hemisphere]);

  // Month growing tip
  const monthTips: Record<number, string[]> = {
    0: ['Start seeds of leeks and onions indoors', 'Check stored crops for rot', 'Order seeds from catalogues'],
    1: ['Chit seed potatoes in a light, frost-free place', 'Sow tomatoes under heat', 'Prune apple and pear trees'],
    2: ['Sow broad beans direct', 'Prepare beds as soil warms', 'Apply Preparation 500 to soil'],
    3: ['Sow carrots, parsnips, beetroot direct', 'Plant out hardened onion sets', 'Sow courgettes under glass'],
    4: ['Harden off tomatoes and courgettes', 'Sow French beans in pots', 'Plant out brassica transplants'],
    5: ['Plant out tender crops after last frost', 'Sow successional salads', 'Earth up potatoes regularly'],
    6: ['Keep on top of weeding', 'Water regularly in dry spells', 'Harvest garlic when tops yellow'],
    7: ['Harvest beans and courgettes daily', 'Sow spring cabbages and kale', 'Collect and dry herbs for winter'],
    8: ['Plant autumn garlic', 'Sow winter salads in cold frames', 'Clear and compost spent crops'],
    9: ['Harvest maincrop potatoes', 'Plant spring bulbs', 'Sow green manures on empty beds'],
    10: ['Dig and enrich beds with compost', 'Plant garlic if not done', 'Protect tender perennials'],
    11: ['Plan next year\'s rotation', 'Check stored apples and root crops', 'Order seed catalogues'],
  };

  // Tips are written for a northern year, so shift them below the equator.
  const tips = monthTips[dataMonth(today.getMonth() + 1) - 1] ?? [];
  const tip = tips[today.getDate() % tips.length];

  return (
    <div className="today-card paper">
      <div className="today-card-header">
        <div className="today-date">
          <span className="today-day-name text-caption">{dayNames[today.getDay()]}</span>
          <span className="today-day-num">{today.getDate()}</span>
          <span className="today-month text-caption">{monthNames[today.getMonth()]} {today.getFullYear()}</span>
        </div>
        <div className="today-moon">
          <span
            className={`today-moon-emoji${astro && isSouthern ? ' today-moon-emoji--mirrored' : ''}`}
          >
            {astro ? MOON_PHASE_EMOJI[astro.phase] : '🌙'}
          </span>
          <span className="today-moon-name text-caption">
            {astro ? `${phaseLabel(astro.phase)} · ${astro.illumination}%` : ' '}
          </span>
        </div>
      </div>

      <div className="today-day-type">
        {astro ? (
          <>
            <span className="text-hand-lg">
              {DAY_TYPE_META[astro.dayType].emoji} {DAY_TYPE_META[astro.dayType].label}
            </span>
            <p className="text-caption" style={{ marginTop: '2px' }}>
              {DAY_TYPE_META[astro.dayType].description}
            </p>
            <p className="text-caption" style={{ marginTop: '4px', color: astro.ascending ? 'var(--sage)' : 'var(--terracotta)' }}>
              {astro.ascending ? '↗ Moon ascending — sow and graft' : '↘ Moon descending — transplant and prune'}
            </p>
          </>
        ) : (
          <>
            <span className="text-hand-lg" style={{ color: 'var(--ink-faint)' }}>Reading the sky…</span>
            <p className="text-caption" style={{ marginTop: '2px' }}>&nbsp;</p>
          </>
        )}
      </div>

      {tip && (
        <div className="today-tip">
          <span className="today-tip-label text-label">This month</span>
          <p className="text-hand" style={{ color: 'var(--ink-light)', lineHeight: 1.5 }}>{tip}</p>
        </div>
      )}
    </div>
  );
}

function HeroBotanicalBg() {
  return (
    <svg viewBox="0 0 800 500" xmlns="http://www.w3.org/2000/svg" className="hero-botanical-bg" aria-hidden="true" preserveAspectRatio="xMidYMid slice">
      {/* Large background botanical elements */}
      <g opacity="0.06" fill="var(--sage)">
        {/* Large leaf top-right */}
        <path d="M650 0 C650 0 700 80 720 150 C740 220 700 280 650 300 C600 320 560 280 540 230 C520 180 540 100 590 50 Z" />
        {/* Stem */}
        <line x1="635" y1="280" x2="620" y2="400" stroke="var(--sage)" strokeWidth="3"/>
        {/* Small leaf top-left */}
        <path d="M50 100 C50 100 80 60 110 50 C140 40 160 70 150 100 C140 130 110 140 80 130 Z" />
        {/* Bottom left botanical */}
        <path d="M0 400 C40 380 80 360 100 320 C120 280 110 240 90 220 C70 200 30 210 10 240 C-10 270 0 350 0 400 Z" />
        {/* Scattered leaves */}
        <path d="M200 50 C210 30 230 25 240 40 C250 55 240 70 225 72 Z" />
        <path d="M380 480 C390 460 415 455 420 470 C425 485 410 500 395 497 Z" />
      </g>
      {/* Fine botanical line work */}
      <g opacity="0.04" stroke="var(--sage)" strokeWidth="1.5" fill="none">
        <circle cx="720" cy="150" r="100"/>
        <circle cx="720" cy="150" r="70"/>
        <circle cx="100" cy="420" r="80"/>
      </g>
    </svg>
  );
}

function MoonCalendarIllustration() {
  const phases = ['🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘'];
  return (
    <div className="moon-calendar-illus">
      <div className="moon-phases-ring">
        {phases.map((moon, i) => {
          const angle = (i / phases.length) * 360 - 90;
          const rad = (angle * Math.PI) / 180;
          const x = 50 + 40 * Math.cos(rad);
          const y = 50 + 40 * Math.sin(rad);
          return (
            <span key={i} className="moon-phase-dot" style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${i * 0.15}s` }}>
              {moon}
            </span>
          );
        })}
        <div className="moon-center-label">
          <span className="text-hand-lg">Lunar</span>
          <span className="text-hand-lg">Calendar</span>
        </div>
      </div>
      <div className="biodynamic-day-labels">
        {[
          { type: 'Root', emoji: '🌱', color: 'var(--terracotta)' },
          { type: 'Flower', emoji: '🌸', color: 'var(--rose)' },
          { type: 'Fruit', emoji: '🍅', color: 'var(--ochre)' },
          { type: 'Leaf', emoji: '🥬', color: 'var(--sage)' },
        ].map(d => (
          <div key={d.type} className="biodynamic-day-badge" style={{ borderColor: d.color, color: d.color }}>
            <span>{d.emoji}</span> {d.type}
          </div>
        ))}
      </div>
    </div>
  );
}
